import { computed, reactive, ref, shallowReactive } from 'vue'

export interface PreloadableAsset {
  url: string
  type: 'image' | 'video'
}

export interface PreloaderState {
  totalBytes: number
  loadedBytes: number
  assetsTotal: number
  assetsLoaded: number
  failed: string[]
}

interface AssetProgress {
  loaded: number
  total: number
}

export type LoadedAsset = HTMLImageElement | HTMLVideoElement

const videoBlobUrls = new Map<string, string>()

const state = reactive<PreloaderState>({
  totalBytes: 0,
  loadedBytes: 0,
  assetsTotal: 0,
  assetsLoaded: 0,
  failed: []
})

const assetProgress = shallowReactive<Map<string, AssetProgress>>(new Map())
const assets = shallowReactive<Map<string, LoadedAsset>>(new Map())
const isLoading = ref(false)
const isComplete = ref(false)

const LOAD_TIMEOUT = 30000

export function useAssetPreloader () {
  const progress = computed(() => {
    if (state.totalBytes === 0) {
      if (state.assetsTotal === 0) return 0
      return Math.round((state.assetsLoaded / state.assetsTotal) * 100)
    }
    return Math.round((state.loadedBytes / state.totalBytes) * 100)
  })

  const hasErrors = computed(() => state.failed.length > 0)

  function getAsset<T extends LoadedAsset> (url: string): T | undefined {
    return assets.get(url) as T | undefined
  }

  function getImage (url: string): HTMLImageElement | undefined {
    return getAsset<HTMLImageElement>(url)
  }

  function getVideo (url: string): HTMLVideoElement | undefined {
    return getAsset<HTMLVideoElement>(url)
  }

  function updateProgress (url: string, loaded: number, total: number) {
    const prev = assetProgress.get(url) || { loaded: 0, total: 0 }

    if (prev.total === 0 && total > 0) {
      state.totalBytes += total
    }

    state.loadedBytes += loaded - prev.loaded
    assetProgress.set(url, { loaded, total })
  }

  function preloadWithXHR (url: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open('GET', url, true)
      xhr.responseType = 'blob'

      xhr.onprogress = (e) => {
        if (e.lengthComputable) {
          updateProgress(url, e.loaded, e.total)
        }
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const blob = xhr.response as Blob
          updateProgress(url, blob.size, blob.size)
          resolve(blob)
        } else {
          reject(new Error(`HTTP ${xhr.status}: ${url}`))
        }
      }

      xhr.onerror = () => reject(new Error(`Network error: ${url}`))
      xhr.ontimeout = () => reject(new Error(`Timeout: ${url}`))
      xhr.timeout = LOAD_TIMEOUT

      xhr.send()
    })
  }

  async function preloadImage (url: string): Promise<HTMLImageElement> {
    const blob = await preloadWithXHR(url)
    const objectUrl = URL.createObjectURL(blob)

    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        URL.revokeObjectURL(objectUrl)
        resolve(img)
      }
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl)
        reject(new Error(`Failed to decode image: ${url}`))
      }
      img.src = objectUrl
    })
  }

  async function preloadVideo (url: string): Promise<HTMLVideoElement> {
    const blob = await preloadWithXHR(url)
    const objectUrl = URL.createObjectURL(blob)

    return new Promise((resolve, reject) => {
      const video = document.createElement('video')
      video.preload = 'auto'
      video.muted = true
      video.playsInline = true

      const cleanup = () => {
        video.removeEventListener('canplaythrough', onReady)
        video.removeEventListener('loadeddata', onReady)
        video.removeEventListener('error', onError)
      }

      const onReady = () => {
        cleanup()
        videoBlobUrls.set(url, objectUrl)
        resolve(video)
      }

      const onError = () => {
        cleanup()
        URL.revokeObjectURL(objectUrl)
        reject(new Error(`Failed to decode video: ${url}`))
      }

      video.addEventListener('canplaythrough', onReady, { once: true })
      video.addEventListener('loadeddata', onReady, { once: true })
      video.addEventListener('error', onError, { once: true })

      video.src = objectUrl
      video.load()

      setTimeout(() => {
        cleanup()
        videoBlobUrls.set(url, objectUrl)
        resolve(video)
      }, 5000)
    })
  }

  async function preloadAsset (asset: PreloadableAsset): Promise<void> {
    try {
      const loaded = asset.type === 'image'
        ? await preloadImage(asset.url)
        : await preloadVideo(asset.url)
      assets.set(asset.url, loaded)
      state.assetsLoaded++
    } catch (error) {
      state.failed.push(asset.url)
      state.assetsLoaded++
      console.warn(`[Preloader] ${error}`)
    }
  }

  async function preloadAll (assetList: PreloadableAsset[]): Promise<void> {
    if (assetList.length === 0) {
      isComplete.value = true
      return
    }

    isLoading.value = true
    state.assetsTotal = assetList.length
    state.assetsLoaded = 0
    state.totalBytes = 0
    state.loadedBytes = 0
    state.failed = []
    assetProgress.clear()

    const concurrency = 4
    const queue = [...assetList]

    const workers = Array(Math.min(concurrency, queue.length))
      .fill(null)
      .map(async () => {
        while (queue.length > 0) {
          const asset = queue.shift()
          if (asset) await preloadAsset(asset)
        }
      })

    await Promise.all(workers)

    isLoading.value = false
    isComplete.value = true
  }

  function reset () {
    // Revoke all video blob URLs to prevent memory leaks
    for (const blobUrl of videoBlobUrls.values()) {
      URL.revokeObjectURL(blobUrl)
    }
    videoBlobUrls.clear()

    state.totalBytes = 0
    state.loadedBytes = 0
    state.assetsTotal = 0
    state.assetsLoaded = 0
    state.failed = []
    assetProgress.clear()
    assets.clear()
    isLoading.value = false
    isComplete.value = false
  }

  return {
    state,
    assets,
    progress,
    isLoading,
    isComplete,
    hasErrors,
    preloadAll,
    getAsset,
    getImage,
    getVideo,
    reset
  }
}
